import { Box, Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { DotDotDot } from 'components/common/layout/dot-dot-dot';
import { VideoPlaceholder } from 'components/common/video/placeholder';
import { CommonVideoPlayerHoC } from 'components/common/video/player';
import useVideoLibrarySidebarStore from 'components/sections/video-library/sidebar/use-video-library-store';
import { VideoPlayerType } from 'enums/video-player.enums';
import { t } from 'i18next';
import { round } from 'lib_ts/classes/math.utilities';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IVideoPlayback } from 'lib_ts/interfaces/i-video';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVideosStore } from 'stores/use-videos-store';

// only used to pick an approximate release time + frame
export const VideoLibrarySidebarReleaseTime = () => {
  const model = useVideoLibrarySidebarStore((s) => s.model);

  const selectedVideo = useVideosStore((s) => s.selectedVideo);

  const [playback, setPlayback] = useState<IVideoPlayback>();

  useEffect(() => {
    if (!selectedVideo?._id) {
      return;
    }

    useVideosStore
      .getState()
      .lazyLoadPlayback(selectedVideo._id)
      .then((result) => setPlayback(result));
  }, [selectedVideo?._id]);

  const aspectRatio = useMemo(
    () =>
      selectedVideo ? VideoHelper.getAspectRatio(selectedVideo) : undefined,
    [selectedVideo]
  );

  const handlePausedUpdate = useCallback(
    (newTime: number) => {
      if (!selectedVideo) {
        return;
      }

      /**
     This check prevents inadvertent release time updates due to rounding discrepancies between frame & time
     unless the user actually scrubs the video player, since onTimeUpdate gets called on mount
    */
      if (newTime === selectedVideo.ReleaseTime) {
        return;
      }

      /** get closest timestamp corresponding directly to a frame */
      const closestFrame = VideoHelper.convertTimeToFrame({
        time: newTime,
        fps: selectedVideo.fps,
        maxFrame: selectedVideo.n_frames,
      });

      useVideoLibrarySidebarStore.getState().mergeModel({
        ReleaseTime: round(
          VideoHelper.convertFrameToTime({
            frame: closestFrame,
            fps: selectedVideo.fps,
            maxFrame: selectedVideo.n_frames,
          }),
          6
        ),
        ReleaseFrame: closestFrame,
      });
    },
    [selectedVideo]
  );

  if (!selectedVideo) {
    return null;
  }

  return (
    <ErrorBoundary identifier="VideoLibrarySidebarPreview">
      <Flex direction="column">
        <Text>{t('videos.set-release-time')}</Text>

        <Box mt="1">
          <Text size="1" color={RADIX.COLOR.SECONDARY}>
            {t('videos.release-time-msg')}
          </Text>
        </Box>

        <Box mt="2">
          {playback ? (
            <CommonVideoPlayerHoC
              playback={playback}
              type={VideoPlayerType.Pitcher}
              startAtTime={model?.ReleaseTime}
              onPausedUpdate={handlePausedUpdate}
            />
          ) : (
            <VideoPlaceholder aspectRatio={aspectRatio}>
              <DotDotDot label="common.loading" />
            </VideoPlaceholder>
          )}
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
